import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { take, BehaviorSubject } from 'rxjs';

import { APP_CONFIG, IAppConfig } from '@app/core/config';
import { PointHelper } from '@app/shared/helper';
import { CommissionDetail, CommissionGroup, CommissionRange, CurrencyExchangeRateResult, CurrencyList } from '@app/shared/models';
import { CommissionActionType } from '@app/shared/types/commission/commission-action-type.enum';
import { CommissionCalcType, CommissionFormParam, CommissionResult, CommissionType } from '../../commissions';
import { RequestType } from '@app/shared/types';


@Injectable({
  providedIn: 'root'
})
export class CommissionService {

  private commissionResultSource = new BehaviorSubject<any>(null);
  commissionResult$ = this.commissionResultSource.asObservable();
  commissionResult: any;
  private commissionData: CommissionGroup;
  exchangeRateResult: any;
  commissionRange: CommissionRange;
  formParams: any;

  fromCurrencyData: CurrencyList;
  toCurrencyData: CurrencyList;

  // number
  minRequestAmount: number;
  maxRequestAmount: number;

  // boolean
  calcCommissionOnPay: boolean;
  exchangeAmountFirst: boolean;
  minOrMaxRequestAmountIsWrong: boolean;
  amountIsSuccess: boolean;

  // types
  actionType: CommissionActionType;
  commissionCalcType: CommissionCalcType;
  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) private appConfig: IAppConfig
  ) { }


  getCommissionGroup(requestModel: CommissionFormParam, url: string, exchangeAmountFirst: boolean): void {
    let payload: any;

    switch (requestModel.requestType) {

      case RequestType.Deposit:
        if (requestModel.actionType === CommissionActionType.SELL_SERVICE) {
          payload = {
            currencyId: requestModel.getCurrencyId,
            serviceId: requestModel.serviceId,
          }
        } else {
          // url += '/Commissions/Deposit/MoneyCurrency';
        }
        break;

      case RequestType.Withdrawal:
        if (requestModel.actionType === CommissionActionType.BUY_SERVICE) {
          payload = {
            currencyId: requestModel.payCurrencyId,
            serviceId: requestModel.serviceId,
          }
        } else {
          payload = {
            payCurrencyId: requestModel.payCurrencyId,
            getCurrencyId: requestModel.getCurrencyId,
            paymentMethodId: requestModel.paymentMethodId,
          }
        }
        break;

      case RequestType.Send:
        if (requestModel.actionType === CommissionActionType.INTERNAL_TRANSFER) {
          payload = {
            payCurrencyId: requestModel.payCurrencyId,
            getCurrencyId: requestModel.getCurrencyId,
            toWallet: requestModel.toWallet,
          }
        } else {
          // url += '/Commissions/Send/MoneyCurrency';
        }
        break;

      case RequestType.Exchange:
        payload = {
          payCurrencyId: requestModel.payCurrencyId,
          getCurrencyId: requestModel.getCurrencyId
        }
        break;

    }

    if (!payload) {
      return
    }

    let params = new HttpParams();
    Object.keys(payload).forEach(item => {
      params = params.append(`${item}`, payload[item]);
    });

    this.http.get(url, { params })
      .pipe(take(1))
      .subscribe(
        response => {
          this.commissionData = response['data'];
          this.formParams = requestModel;
          this.commissionData.commission = this.handelNullCommission(this.commissionData.commission);
          this.setData(requestModel.fromCurrencyData, requestModel.toCurrencyData, requestModel.actionType, requestModel.amount, exchangeAmountFirst);
          this.setCommissionResult();
          return this.commissionData;
        }
      );
  }

  setCommissionResult(): void {
    let result = this.calculateCommission();
    if (!result.success) {
      this.updateCommissionResult(result);
      return;
    }
    result.commissionCalcType = this.commissionCalcType;
    result = this.checkForSpecialwallet(result);
    result.refreshTime = this.commissionData.refreshTime;
    result.point = this.calculatePoint(result, this.actionType, this.commissionData['echangeRatePoint']);
    this.updateCommissionResult(result);

  }

  private checkForSpecialwallet(result: CommissionResult): CommissionResult {
    if (!this.commissionData.specialInfo || !this.commissionData.specialInfo.IsSpecial) {
      return result;
    }

    const minRequestAmount = this.getMinRequestAmountForSpecialWallet(this.fromCurrencyData, this.commissionData.specialInfo.minimumReceiveAmount, result);
    result.minAmount = minRequestAmount;
    if (result.payOrGetAmount < minRequestAmount) {
      result.success = false;
      result.amountIsWrong = true;
    }
    return result;
  }

  private updateCommissionResult(commissionResult: CommissionResult): void {
    this.commissionResultSource.next(commissionResult);
    this.commissionResult = commissionResult;
  }


  private calculateCommission(): CommissionResult {
    const precisionFrom: number = this.fromCurrencyData.precision;
    const precisionTo: number = this.toCurrencyData.precision;

    this.calcCommissionOnPay = this.isCommissionOnPay();
    const requestAmount: number = this.toFixedScale(this.formParams.amount, precisionFrom);

    const amountIsWrong = this.amountIsWrong();
    if (amountIsWrong) {
      const result: CommissionResult = {
        success: false,
        amountIsWrong: true,
        minAmount: this.minRequestAmount,
        maxAmount: this.maxRequestAmount
      };
      return result;
    }

    if (
      this.actionType == CommissionActionType.BETWEEN_MY_WALLETS &&
      this.fromCurrencyData.id === this.toCurrencyData.id
    ) {
      return this.resultForBetweenMyWallet();
    }

    let convertedAmount: number = this.exchangeAmountFirst
      ? this.convertAmount(requestAmount, this.exchangeRateResult, precisionTo)
      : requestAmount;

    // result for none commission
    if (this.commissionData.commission.commissionType == CommissionType.NONE) {
      return this.resultForNoneCommission(requestAmount, convertedAmount, this.exchangeAmountFirst);

    }

    const commissionRange = this.getCommissionRange(convertedAmount);

    let { commission, actualPayOrGetAmount } = this.calculateCommissionByType(this.commissionData.commission.commissionType, convertedAmount, commissionRange,
      this.exchangeAmountFirst ? precisionTo : precisionFrom);

    // const calculatedAmount: number = actualPayOrGetAmount;

    if (!this.exchangeAmountFirst) {

      if (this.actionType === CommissionActionType.INTERNAL_TRANSFER) {
        actualPayOrGetAmount = this.convertAmount(this.formParams.amount, this.exchangeRateResult, precisionTo);

      } else {
        actualPayOrGetAmount = this.convertAmount(actualPayOrGetAmount, this.exchangeRateResult, precisionTo);

      }
      commission = commission;
      convertedAmount = actualPayOrGetAmount;
    }

    // result for normal situation
    const result: CommissionResult = {
      success: true,
      requestedAmount: requestAmount,
      requestedPrecision: precisionFrom,
      requestedCurrencyCode: this.fromCurrencyData.code,
      requestedCurrency: this.fromCurrencyData,
      exchangeRate: this.exchangeRateResult.rate,
      exchangeRateInverse: this.exchangeRateResult.inverse,
      exchangeCurrencyCode: this.getExchangeServiceName(),
      exchangeRateIsOld: false,
      convertedAmount: this.calcCommissionOnPay ? actualPayOrGetAmount : this.convertAmount(requestAmount, this.exchangeRateResult, this.toCurrencyData.precision),
      commissionType: this.commissionData.commission.commissionType,
      minCommission: this.commissionData.commission.minCommission,
      maxCommission: this.commissionData.commission.maxCommission,
      commissionInitialFee: commissionRange.initialFee,
      commissionPercent: commissionRange.percent,
      commissionAmount: commission,
      payOrGetAmount: actualPayOrGetAmount,
      payOrGetPrecision: this.toCurrencyData.precision,
      payOrGetCurrencyCode: this.toCurrencyData.code,
      payOrGetCurrency: this.toCurrencyData,
      calculatedAmount: this.getCalculatedAmount(requestAmount, this.actionType, commission,
        commissionRange.initialFee, this.fromCurrencyData.precision),
      minAmount: this.minRequestAmount,
      maxAmount: this.maxRequestAmount
    };
    return result;

  }

  private getCalculatedAmount(
    requestedAmount: number, actionType: CommissionActionType,
    commissionAmount: number, initialFee: number, requestedPrecision: number
  ): number {
    const commissionType = this.commissionData.commission.commissionType;

    if (this.commissionData.commission.commissionType == CommissionType.NONE)
      return requestedAmount;

    switch (actionType) {
      case CommissionActionType.INTERNAL_TRANSFER:
      case CommissionActionType.BUY_SERVICE:
        return commissionType == CommissionType.COMMISSION
          ? this.toFixedScale((requestedAmount + commissionAmount + initialFee), requestedPrecision)
          : this.toFixedScale((requestedAmount - commissionAmount - initialFee), requestedPrecision);
      default:
        return commissionType == CommissionType.COMMISSION
          ? this.toFixedScale((requestedAmount - commissionAmount - initialFee), requestedPrecision)
          : this.toFixedScale((requestedAmount + commissionAmount + initialFee), requestedPrecision);
    }

  }

  private getExchangeServiceName(): string {
    if (this.exchangeRateResult.actionIsBuy) {
      return this.fromCurrencyData.code
    }
    return this.toCurrencyData.code;
  }

  private resultForBetweenMyWallet(): CommissionResult {
    const result: CommissionResult =
    {
      success: true,
      requestedAmount: this.toFixedScale(this.formParams.amount, this.fromCurrencyData.precision),
      requestedCurrency: this.fromCurrencyData,
      requestedPrecision: this.fromCurrencyData.precision,
      requestedCurrencyCode: this.fromCurrencyData.code,
      commissionType: CommissionType.NONE,
      payOrGetAmount: this.formParams.amount,
      exchangeRate: 1,
      convertedAmount: this.formParams.amount,
      payOrGetPrecision: this.toCurrencyData.precision,
      payOrGetCurrencyCode: this.toCurrencyData.code,
      payOrGetCurrency: this.toCurrencyData
    };
    return result;
  }

  private resultForNoneCommission(
    requestedAmount: number,
    convertedAmount: number,
    exchangeAmountFirst: boolean
  ): CommissionResult {
    const result: CommissionResult = {
      success: true,
      commissionType: this.commissionData.commission.commissionType,
      requestedAmount: requestedAmount,
      requestedCurrency: this.fromCurrencyData,
      requestedPrecision: this.fromCurrencyData.precision,
      requestedCurrencyCode: this.fromCurrencyData.code,
      exchangeRate: this.exchangeRateResult.rate,
      exchangeRateInverse: this.exchangeRateResult.inverse,
      exchangeCurrencyCode: this.getExchangeServiceName(),
      exchangeRateIsOld: false,
      convertedAmount: exchangeAmountFirst ? convertedAmount : this.convertAmount(convertedAmount, this.exchangeRateResult, this.toCurrencyData.precision),
      minCommission: -1,
      maxCommission: -1,
      payOrGetAmount: this.convertAmount(requestedAmount, this.exchangeRateResult, this.toCurrencyData.precision),
      payOrGetPrecision: this.toCurrencyData.precision,
      payOrGetCurrencyCode: this.toCurrencyData.code,
      payOrGetCurrency: this.toCurrencyData,
      calculatedAmount: requestedAmount
    };
    return result;
  }

  private isCommissionOnPay(): boolean {
    return this.actionType === CommissionActionType.BUY_SERVICE ||
      this.actionType === CommissionActionType.INTERNAL_TRANSFER;
  }

  private setData(fromCurrencyData: CurrencyList, toCurrencyData: CurrencyList, actionType: CommissionActionType, amount: number, exchangeAmountFirst: boolean): void {
    this.actionType = actionType;
    this.fromCurrencyData = fromCurrencyData;
    this.toCurrencyData = toCurrencyData;
    this.fromCurrencyData.code = this.fromCurrencyData.name;
    //  this.toCurrencyData.code = this.toCurrencyData.name;
    this.formParams.amount = amount;
    this.exchangeAmountFirst = exchangeAmountFirst;
    this.exchangeRateResult = this.commissionData['echangeRateForm'];
    this.exchangeRateResult.rate = this.exchangeRateResult.exchangeRate;
  }

  private handelNullCommission(commission: CommissionDetail): CommissionDetail {
    if (commission === null) {
      commission = {
        maxCommission: -1,
        minCommission: -1,
        commissionType: CommissionType.NONE,
        ranges: null
      }
    }
    return commission
  }

  private calculateCommissionByType(commissionType: CommissionType, convertedAmount: number, commissionRange: CommissionRange, precision: number): any {
    let commission = 0;
    let actualPayOrGetAmount = 0;

    if (commissionType === CommissionType.NONE) {
      commission = null;
      actualPayOrGetAmount = convertedAmount;
      return { commission: commission, actualPayOrGetAmount: actualPayOrGetAmount };
    }


    const minCommission = this.commissionData.commission.minCommission;
    const maxCommission = this.commissionData.commission.maxCommission;
    const precent = commissionRange.percent;
    const initialFee = commissionRange.initialFee;


    if ((!this.calcCommissionOnPay && commissionType === CommissionType.COMMISSION) || (this.calcCommissionOnPay && commissionType === CommissionType.BONUS)) {
      const amountWithDeductionInitialFee = this.toFixedScale(convertedAmount - initialFee, precision);
      commission = this.calculateCommissionFullParam(amountWithDeductionInitialFee, minCommission, maxCommission, precent, precision);
      actualPayOrGetAmount = this.toFixedScale(amountWithDeductionInitialFee - commission, precision);

    } else if ((!this.calcCommissionOnPay && commissionType === CommissionType.BONUS) || (this.calcCommissionOnPay && commissionType === CommissionType.COMMISSION)) {

      commission = this.calculateCommissionFullParam(convertedAmount, minCommission, maxCommission, precent, precision);
      actualPayOrGetAmount = this.toFixedScale(convertedAmount + commission + initialFee, precision);
    }


    return { commission: commission, actualPayOrGetAmount: actualPayOrGetAmount };
  }


  /**********************************************************
    Min Request Amount
  **********************************************************/

  private getMinRequestAmount(commissionType: CommissionType, exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, precision: number): number {


    if (!this.calcCommissionOnPay && commissionType === CommissionType.COMMISSION || this.calcCommissionOnPay && commissionType === CommissionType.BONUS) {

      return this.getMinRequestForComissionType(exchangeRate, exchangeAmountFirst, precision);
    }

    if (!this.calcCommissionOnPay && commissionType === CommissionType.BONUS || this.calcCommissionOnPay && commissionType === CommissionType.COMMISSION) {

      return this.getMinRequestAmountForBonusType(exchangeRate, exchangeAmountFirst, precision);
    }

    return -2;
  }

  private getMinRequestForComissionType(exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, precision: number): number {


    const firstRange = this.commissionData.commission.ranges[0];
    const initialFee = firstRange.initialFee;
    const targetPriceForCommission = firstRange.from + 0.00000001;

    let commission = this.calculateCommissionPartialParam(targetPriceForCommission, precision);
    if (commission < 0) {
      commission = 0;
    }

    const minOrderAmount = targetPriceForCommission + commission + initialFee;

    return this.convertedMinMaxRequestAmount(exchangeRate, exchangeAmountFirst, minOrderAmount * 1.5, precision);
  }

  private getMinRequestAmountForBonusType(exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, precision: number): number {

    const firstRange: CommissionRange = this.commissionData.commission.ranges[0];

    const targetPriceForCommission = firstRange.from + 0.00000001;

    return this.convertedMinMaxRequestAmount(exchangeRate, exchangeAmountFirst, targetPriceForCommission * 1.5, precision);
  }

  private amountIsWrong(): boolean {
    const commissionType = this.commissionData.commission.commissionType;
    this.minRequestAmount = this.getMinRequestAmount(commissionType, this.exchangeRateResult, this.exchangeAmountFirst, this.fromCurrencyData.precision);
    this.maxRequestAmount = this.getMaxRequestAmount(commissionType, this.exchangeRateResult, this.exchangeAmountFirst, this.fromCurrencyData.precision);
    if (
      (this.minRequestAmount > -1 && this.formParams.amount < this.minRequestAmount) ||
      (this.maxRequestAmount > -1 && this.formParams.amount > this.maxRequestAmount)
    ) {
      return true
    }
    return false;
  }


  /**********************************************************
    Max Request Amount
  **********************************************************/

  private getMaxRequestAmount(commissionType: CommissionType, exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, precision: number): number {

    if (!this.calcCommissionOnPay && commissionType === CommissionType.COMMISSION || this.calcCommissionOnPay && commissionType === CommissionType.BONUS) {

      return this.getMaxRequestForComissionType(exchangeRate, exchangeAmountFirst, precision);
    }

    if (!this.calcCommissionOnPay && commissionType === CommissionType.BONUS || this.calcCommissionOnPay && commissionType === CommissionType.COMMISSION) {

      return this.getMaxRequestAmountForBonusType(exchangeRate, exchangeAmountFirst, precision);
    }

    return -2;
  }

  private getMaxRequestForComissionType(exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, precision: number): number {

    const lastRange = this.commissionData.commission.ranges[(this.commissionData.commission.ranges.length - 1)];

    const targetPriceForCommission = lastRange.to;
    if (targetPriceForCommission <= 0) {
      return -1;
    }

    return this.convertedMinMaxRequestAmount(exchangeRate, exchangeAmountFirst, targetPriceForCommission, precision);
  }

  private getMaxRequestAmountForBonusType(exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, precision: number): number {

    const lastRange = this.commissionData.commission.ranges[(this.commissionData.commission.ranges.length - 1)];


    const targetPriceForCommission = lastRange.to;
    if (targetPriceForCommission <= 0) {
      return -1;
    }

    return this.convertedMinMaxRequestAmount(exchangeRate, exchangeAmountFirst, targetPriceForCommission, precision);
  }

  private getMinRequestAmountForSpecialWallet(fromCurrency: CurrencyList, minRecieveAmount: number, result: CommissionResult): number {
    var amount = result.exchangeCurrencyCode == fromCurrency.code ? minRecieveAmount * result.exchangeRate : minRecieveAmount / result.exchangeRate;
    return this.toFixedScale(amount, fromCurrency.precision);
  }


  /**********************************************************
  Convert Amounts
  **********************************************************/

  private convertedMinMaxRequestAmount(exchangeRate: CurrencyExchangeRateResult, exchangeAmountFirst: boolean, amount: number, precision: number): number {

    const rate = exchangeAmountFirst ? exchangeRate.rate : 1;

    const convertWithExchangeRate = exchangeRate.actionIsBuy
      ? amount * rate
      : amount / rate;
    return this.toFixedScale(convertWithExchangeRate, precision);
  }

  /**********************************************************
  Calculate Commission
  **********************************************************/

  private calculateCommissionPartialParam(amount: number, precision: number): number {
    const minCommission: number = this.commissionData.commission.minCommission;
    const maxCommission: number = this.commissionData.commission.maxCommission;
    const range: CommissionRange = this.getCommissionRange(amount);

    return this.calculateCommissionFullParam(amount, minCommission, maxCommission, range.percent, precision);
  }

  private calculateCommissionFullParam(amount: number, minCommission: number, maxCommission: number,
    commission: number, precision: number): number {

    const calcCommission = (amount * commission) / 100;

    const calcCommissionRound = this.toFixedScale(calcCommission, precision);
    if (calcCommissionRound <= 0) {
      return 0;
    }

    if (minCommission !== -1 && calcCommissionRound <= minCommission) {
      this.commissionCalcType = CommissionCalcType.MINIMUM;
      return minCommission;
    }

    if (maxCommission !== -1 && calcCommissionRound >= maxCommission) {
      this.commissionCalcType = CommissionCalcType.MAXIMUM;
      return maxCommission;
    }

    return calcCommissionRound;
  }

  getCommissionRange(amount: number): CommissionRange {
    if (!this.commissionData.commission.ranges) {
      return;
    }
    for (const cr of this.commissionData.commission.ranges) {

      if (cr.to === -1 && amount > cr.from) {
        return cr;
      }

      if (amount > cr.from && amount <= cr.to) {
        return cr;
      }
    }

    return null;
  }

  calculatePoint(commissionResult: CommissionResult, actionType: CommissionActionType, exchangeRate: CurrencyExchangeRateResult): number {
    const point = this.commissionData.point;
    let amount = commissionResult.requestedAmount;
    let currency = commissionResult.requestedCurrency;

    if (!point || point.howManyCurrency === 0 || point.howManyPoints === 0) {
      return null;
    }

    if (actionType == CommissionActionType.BUY_SERVICE) {
      currency = commissionResult.payOrGetCurrency;
      amount = commissionResult.payOrGetAmount;
    }

    const calcPoint = PointHelper.getPoint(amount, point, currency, exchangeRate);

    return this.toFixedScale(calcPoint, this.appConfig.pointPrecision);
  }

  private convertAmount(amount: number, exchangeRate: CurrencyExchangeRateResult, scale: number): number {
    if (amount <= 0) {
      return 0;
    }

    if (exchangeRate.rate <= 0) {
      return amount;
    }

    if (exchangeRate.actionIsBuy) {
      return this.toFixedScale(amount / exchangeRate.rate, scale);
    }

    return this.toFixedScale(amount * exchangeRate.rate, scale);
  }

  /**********************************************************
  Utils
  **********************************************************/

  private toFixedScale(num, scale: number): number {
    const converNume = parseFloat(num).toFixed((scale + 1));
    return parseFloat(converNume.substring(0, (converNume.length - 1)));
  }

}

